import { updateFirmProfilePictureV1, updateFirmV1 } from "@/lib/data/requests";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Firm } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useUpdateFirm = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateFirmV1>[0]) =>
      updateFirmV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firm],
        (oldData: APIResponse<Firm> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: { ...oldData.data, ...data },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.firm]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firm], context.previousData);
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating firm");
      }
    },

    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.firm] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.currentUser] }),
      ]);
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Firm updated successfully");
      }
    },
  });
};

export const useUpdateFirmProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      firm: Pick<Firm, "dateModified">;
      file: File;
    }) => {
      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "firm-profile-picture",
      )({
        clientMethod: "put_object",
        fileName: data.file.name,
      });

      await fetch(presignedURLRes.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": data.file.type,
        },
        body: data.file,
      });

      return await updateFirmProfilePictureV1({
        dateModified: data.firm.dateModified,
        profilePicturePresignedUrlKey: presignedURLRes.data.key,
      });
    },
    onSuccess: () => {
      toast.success("Firm logo updated successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating firm logo");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firm],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.currentUser],
      });
    },
  });
};

export const getFirmProfilePictureOptions = (key?: string) => {
  return {
    queryKey: [queryKeys.firm, "photo", key],
    enabled: Boolean(key),
    queryFn: async () => {
      if (!key) {
        return null;
      }
      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "firm-profile-picture",
      )({
        clientMethod: "get_object",
        key,
      });
      const file = await fetch(presignedURLRes.data.presignedUrl, {
        method: "GET",
      });
      return file;
    },
  };
};
